// Radha — staff-side view of customer portal messages: what a customer sent
// through their portal link (dispute, question, payment reference), with a
// reply that writes straight back to their portal thread.
import React, { useState } from "react";

import { usePortalMessages, useReplyToPortalMessage, type PortalMessage } from "../hooks/usePortalMessages";
import { fmtDateTime } from "../lib/money";

const KIND_LABEL: Record<PortalMessage["kind"], string> = {
  dispute: "Dispute", question: "Question", payment_proof: "Payment reference",
};
const KIND_COLOR: Record<PortalMessage["kind"], string> = {
  dispute: "var(--status-critical)", question: "var(--accent-purple)", payment_proof: "var(--status-good)",
};

function MessageRow({ m, canWrite }: { m: PortalMessage; canWrite: boolean }) {
  const reply = useReplyToPortalMessage();
  const [text, setText] = useState(m.staff_reply ?? "");
  const [note, setNote] = useState<string | null>(null);

  const send = (resolve: boolean) => {
    if (!text.trim()) return;
    setNote(null);
    reply.mutate({ id: m.id, reply: text, resolve }, {
      onSuccess: () => setNote(resolve ? "Replied & resolved" : "Replied"),
      onError: (e) => setNote(e instanceof Error ? e.message : String(e)),
    });
  };

  return (
    <div className="sanctuary-card" style={{ padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
        <span>
          <strong>{m.customer_name}</strong>
          <span style={{ marginLeft: 8, fontSize: 11.5, color: KIND_COLOR[m.kind], border: `1px solid ${KIND_COLOR[m.kind]}`, borderRadius: 6, padding: "1px 6px" }}>
            {KIND_LABEL[m.kind]}
          </span>
        </span>
        <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
          {fmtDateTime(m.created_at)} · <span style={{ color: m.status === "open" ? "var(--status-warning)" : "var(--status-good)" }}>{m.status}</span>
        </span>
      </div>
      <p style={{ fontSize: 13, margin: "6px 0" }}>{m.body}</p>
      {m.payment_reference && <p style={{ fontSize: 12, color: "var(--fg-muted)", margin: "0 0 6px" }}>Ref: {m.payment_reference}</p>}

      {canWrite && (
        <div style={{ marginTop: 6 }}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Reply to the customer…"
            style={{ width: "100%", padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, background: "var(--surface)", color: "var(--fg)", resize: "vertical" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center" }}>
            <button onClick={() => send(false)} disabled={reply.isPending || !text.trim()}
              style={{ padding: "4px 10px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 6, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
              Save reply
            </button>
            <button onClick={() => send(true)} disabled={reply.isPending || !text.trim()}
              style={{ padding: "4px 10px", fontSize: 12, border: 0, borderRadius: 6, background: "var(--status-good)", color: "#fff", cursor: "pointer" }}>
              Reply &amp; resolve
            </button>
            {note && <span style={{ fontSize: 12, color: "var(--status-good)" }}>{note}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export function PortalMessages({ canWrite }: { canWrite: boolean }) {
  const { data: messages, isLoading } = usePortalMessages();
  const open = (messages ?? []).filter((m) => m.status === "open");
  const resolved = (messages ?? []).filter((m) => m.status === "resolved");

  if (isLoading) return null;
  if ((messages ?? []).length === 0) return null;

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Portal messages</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px" }}>
        Sent by customers through their portal link — disputes, questions, and payment references.
      </p>
      {open.length > 0 && (
        <>
          <div style={{ font: "600 12.5px Inter", color: "var(--status-warning)", marginBottom: 6 }}>Open ({open.length})</div>
          {open.map((m) => <MessageRow key={m.id} m={m} canWrite={canWrite} />)}
        </>
      )}
      {resolved.length > 0 && (
        <>
          <div style={{ font: "600 12.5px Inter", color: "var(--fg-muted)", margin: "14px 0 6px" }}>Resolved ({resolved.length})</div>
          {resolved.map((m) => <MessageRow key={m.id} m={m} canWrite={canWrite} />)}
        </>
      )}
    </div>
  );
}
